import React, { useState, useMemo, useEffect } from "react";
import {
  Search,
  Calendar,
  Phone,
  CreditCard,
  Check,
  X,
  Filter,
  Eye,
  Download,
  MoreVertical,
  Users,
  Star,
  Clock,
  MapPin,
  Loader2,
  RefreshCcw,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  Package,
  Activity,
  DollarSign,
  PieChart,
  BarChart3,
  CalendarDays,
  Ticket as TicketIcon,
} from "lucide-react";
import { apiUrl } from "../../constants";
import { createPortal } from "react-dom";
import axios from "axios";
import { scrollToTop } from "../../utils";
const Modal = ({ children, isOpen }) => {
  if (!isOpen) return null;

  return createPortal(children, document.body);
};

const Ticket = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketsData, setTicketsData] = useState([]);
  const [statsData, setStatsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // New filter states
  const [dateFilter, setDateFilter] = useState("");
  const [packageFilter, setPackageFilter] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [ticketTypeFilter, setTicketTypeFilter] = useState("all");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  useEffect(() => {
    scrollToTop();
  }, []);
  const getAllTicket = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiUrl}/ticket/get-all-tickets-admin`
      );
      const data = response.data;

      console.log(data, "ticket data");
      if (data.success) {
        setTicketsData(data.tickets || []);
        setStatsData(data.stats || {});
      } else {
        setError("Failed to fetch tickets");
      }
    } catch (err) {
      setError("Error fetching tickets: " + err.message);
      console.error("Error fetching tickets:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllTicket();
  }, []);

  useEffect(() => {
    if (selectedTicket) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [selectedTicket]);

  const getPackageNames = (details) => {
    if (!details || !Array.isArray(details)) return [];

    return details
      .map((detail) => {
        if (detail.packageType === "unlimited" && detail.unlimitedPackageData) {
          return detail.unlimitedPackageData.name;
        } else if (detail.packageType === "regular") {
          return "Regular Package";
        }
        return "Unknown Package";
      })
      .filter((name) => name !== "Unknown Package");
  };

  const getTotalActivities = (details) => {
    if (!details || !Array.isArray(details)) return 0;

    return details.reduce((total, detail) => {
      if (
        detail.packageType === "unlimited" &&
        detail.unlimitedPackageData &&
        detail.unlimitedPackageData.activities
      ) {
        return total + detail.unlimitedPackageData.activities.length;
      }
      return total;
    }, 0);
  };

  // Filter and sort tickets
  const filteredAndSortedTickets = useMemo(() => {
    let filtered = ticketsData.filter((ticket) => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        ticket.short_id.toLowerCase().includes(searchLower) ||
        ticket.phone_no.includes(searchTerm) ||
        ticket.ticket_holder_name.toLowerCase().includes(searchLower); // Added this line

      const matchesDate =
        !dateFilter ||
        new Date(ticket.fun_date).toDateString() ===
          new Date(dateFilter).toDateString();

      const ticketPackages = getPackageNames(ticket.details);
      const matchesPackage =
        !packageFilter ||
        ticketPackages.some((pkg) =>
          pkg.toLowerCase().includes(packageFilter.toLowerCase())
        );

      const matchesPaymentStatus =
        paymentStatusFilter === "all" ||
        (paymentStatusFilter === "verified" && ticket.payment_verified) ||
        (paymentStatusFilter === "pending" && !ticket.payment_verified);

      const matchesTicketType =
        ticketTypeFilter === "all" ||
        (ticketTypeFilter === "unlimited" &&
          ticket.count_unlimited_ticket > 0) ||
        (ticketTypeFilter === "regular" && ticket.count_regular_ticket > 0);

      return (
        matchesSearch &&
        matchesDate &&
        matchesPackage &&
        matchesPaymentStatus &&
        matchesTicketType
      );
    });

    return filtered.sort((a, b) => {
      const dateA = new Date(a.fun_date);
      const dateB = new Date(b.fun_date);

      if (sortOrder === "desc") {
        return dateB - dateA;
      } else {
        return dateA - dateB;
      }
    });
  }, [
    ticketsData,
    searchTerm,
    sortOrder,
    dateFilter,
    packageFilter,
    paymentStatusFilter,
    ticketTypeFilter,
  ]);

  // Advanced analytics calculations
  const getAdvancedStats = useMemo(() => {
    const dataToAnalyze = filteredAndSortedTickets;

    const totalUnlimitedTickets = dataToAnalyze.reduce(
      (sum, ticket) => sum + ticket.count_unlimited_ticket,
      0
    );
    const totalRegularTickets = dataToAnalyze.reduce(
      (sum, ticket) => sum + ticket.count_regular_ticket,
      0
    );

    // Fixed revenue calculations
    let unlimitedRevenue = 0;
    let regularRevenue = 0;

    // Package wise analysis - Fixed
    const packageStats = {};

    dataToAnalyze.forEach((ticket) => {
      // Calculate unlimited revenue first
      let ticketUnlimitedRevenue = 0;

      ticket.details.forEach((detail) => {
        if (detail.packageType === "unlimited" && detail.unlimitedPackageData) {
          const packageName = detail.unlimitedPackageData.name;
          const packagePrice = detail.unlimitedPackageData.price || 0;

          // Add to unlimited revenue
          unlimitedRevenue += packagePrice;
          ticketUnlimitedRevenue += packagePrice;

          // Package stats
          if (!packageStats[packageName]) {
            packageStats[packageName] = {
              count: 0,
              revenue: 0,
              activities: detail.unlimitedPackageData.activities
                ? detail.unlimitedPackageData.activities.length
                : 0,
            };
          }
          packageStats[packageName].count += 1;
          packageStats[packageName].revenue += packagePrice;
        }
      });

      // Calculate regular package revenue = total_amount - unlimited_revenue
      const hasRegularPackage = ticket.details.some(
        (detail) => detail.packageType === "regular"
      );
      if (hasRegularPackage && ticket.count_regular_ticket > 0) {
        const regularPrice = ticket.total_amount - ticketUnlimitedRevenue;
        regularRevenue += regularPrice;

        if (!packageStats["Regular Package"]) {
          packageStats["Regular Package"] = {
            count: 0,
            revenue: 0,
            activities: 5, // Default activities for regular package
          };
        }
        packageStats["Regular Package"].count += ticket.count_regular_ticket;
        packageStats["Regular Package"].revenue += regularPrice;
      }
    });

    // Date wise analysis - Fixed to use filtered data
    const dateStats = {};
    dataToAnalyze.forEach((ticket) => {
      const date = new Date(ticket.fun_date).toDateString();
      if (!dateStats[date]) {
        dateStats[date] = { count: 0, revenue: 0 };
      }
      dateStats[date].count += 1;
      dateStats[date].revenue += ticket.total_amount;
    });

    // Calculate total revenue from filtered tickets
    const totalRevenue = dataToAnalyze.reduce(
      (sum, ticket) => sum + ticket.total_amount,
      0
    );

    return {
      totalUnlimitedTickets,
      totalRegularTickets,
      unlimitedRevenue,
      regularRevenue,
      packageStats,
      dateStats,
      totalRevenue, // Add this for consistency
      avgTicketValue: dataToAnalyze.length
        ? totalRevenue / dataToAnalyze.length
        : 0,
    };
  }, [filteredAndSortedTickets]);

  // Get unique package names for filter
  const uniquePackages = useMemo(() => {
    const packages = new Set();
    ticketsData.forEach((ticket) => {
      getPackageNames(ticket.details).forEach((name) => packages.add(name));
    });
    return Array.from(packages);
  }, [ticketsData]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedTickets.length / itemsPerPage);
  const paginatedTickets = filteredAndSortedTickets.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (date.getFullYear() === 1970) {
      return "Date not set";
    }
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const getTicketTypeColor = (ticket) => {
    if (ticket.count_unlimited_ticket > 0) return "from-purple-500 to-pink-500";
    if (ticket.count_regular_ticket > 0) return "from-blue-500 to-cyan-500";
    return "from-gray-500 to-gray-600";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-8 border border-white/20">
          <div className="flex items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            <span className="text-xl font-semibold text-gray-700">
              Loading tickets...
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-8 border border-white/20 text-center">
          <X className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Error Loading Tickets
          </h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-2xl hover:from-blue-600 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-pink-400/20 to-orange-400/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 p-4 md:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-2">
                  Ticket Management Dashboard
                </h1>
                <p className="text-gray-600 text-lg">
                  Comprehensive analytics and ticket management
                </p>
              </div>

              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-teal-600 text-white rounded-2xl hover:from-green-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center gap-2"
              >
                <RefreshCcw className="h-5 w-5" />
                Refresh Data
              </button>
            </div>
          </div>

          {/* Advanced Filters */}
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6 mb-8 border border-white/20">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
              {/* Search */}
              <div className="lg:col-span-2 relative ">
                <input
                  type="text"
                  placeholder="Search Tickets by Name Ticket ID or Phone"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white/50"
                />
              </div>

              {/* Date Filter */}
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="w-full  pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white/50"
                />
              </div>

              {/* Package Filter */}
              <div className="relative">
                <Package className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none z-10" />
                <select
                  value={packageFilter}
                  onChange={(e) => setPackageFilter(e.target.value)}
                  className="w-full h-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white/50 appearance-none text-gray-900"
                >
                  <option value="" disabled className="text-gray-400">
                    Select a package...
                  </option>
                  {uniquePackages.map((pkg) => (
                    <option key={pkg} value={pkg} className="text-gray-900">
                      {pkg}
                    </option>
                  ))}
                </select>
              </div>

              {/* Payment Status Filter */}
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <select
                  value={paymentStatusFilter}
                  onChange={(e) => setPaymentStatusFilter(e.target.value)}
                  className="w-full h-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white/50 appearance-none"
                >
                  <option value="all">All Status</option>
                  <option value="verified">Verified</option>
                  <option value="pending">Pending</option>
                </select>
              </div>

              {/* Ticket Type Filter */}
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <select
                  value={ticketTypeFilter}
                  onChange={(e) => setTicketTypeFilter(e.target.value)}
                  className="w-full h-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white/50 appearance-none"
                >
                  <option value="all">All Types</option>
                  <option value="unlimited">Unlimited</option>
                  <option value="regular">Regular</option>
                </select>
              </div>
            </div>

            {/* Filter Summary */}
            <div className="mt-4 flex flex-wrap gap-2">
              {searchTerm && (
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                  Search: {searchTerm}
                </span>
              )}
              {dateFilter && (
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                  Date: {formatDate(dateFilter)}
                </span>
              )}
              {packageFilter && (
                <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                  Package: {packageFilter}
                </span>
              )}
              {paymentStatusFilter !== "all" && (
                <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm">
                  Status: {paymentStatusFilter}
                </span>
              )}
              {ticketTypeFilter !== "all" && (
                <span className="px-3 py-1 bg-pink-100 text-pink-800 rounded-full text-sm">
                  Type: {ticketTypeFilter}
                </span>
              )}
            </div>
          </div>

          {/* Enhanced Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-8">
            {/* Total Tickets */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <TicketIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {filteredAndSortedTickets.length}
                  </p>
                  <p className="text-sm text-gray-600">Total Tickets</p>
                </div>
              </div>
            </div>

            {/* Unlimited Tickets */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/10 rounded-lg">
                  <Star className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {getAdvancedStats.totalUnlimitedTickets}
                  </p>
                  <p className="text-sm text-gray-600">Unlimited</p>
                </div>
              </div>
            </div>

            {/* Regular Tickets */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-500/10 rounded-lg">
                  <Package className="h-5 w-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {getAdvancedStats.totalRegularTickets}
                  </p>
                  <p className="text-sm text-gray-600">Regular</p>
                </div>
              </div>
            </div>

            {/* Verified Tickets */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/10 rounded-lg">
                  <Check className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {
                      filteredAndSortedTickets.filter((t) => t.payment_verified)
                        .length
                    }
                  </p>
                  <p className="text-sm text-gray-600">Verified</p>
                </div>
              </div>
            </div>

            {/* Pending Tickets */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <Clock className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {
                      filteredAndSortedTickets.filter(
                        (t) => !t.payment_verified
                      ).length
                    }
                  </p>
                  <p className="text-sm text-gray-600">Pending</p>
                </div>
              </div>
            </div>

            {/* Total Revenue */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/10 rounded-lg">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">
                    {formatAmount(
                      filteredAndSortedTickets.reduce(
                        (sum, t) => sum + t.total_amount,
                        0
                      )
                    )}
                  </p>
                  <p className="text-sm text-gray-600">Revenue</p>
                </div>
              </div>
            </div>
          </div>

          {/* Revenue Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6 border border-white/20">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-purple-600" />
                Revenue Breakdown
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-purple-50 rounded-xl">
                  <span className="font-medium text-purple-700">
                    Unlimited Packages
                  </span>
                  <span className="font-bold text-purple-900">
                    {formatAmount(getAdvancedStats.unlimitedRevenue)}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-xl">
                  <span className="font-medium text-blue-700">
                    Coins Packages
                  </span>
                  <span className="font-bold text-blue-900">
                    {formatAmount(getAdvancedStats.regularRevenue)}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-xl">
                  <span className="font-medium text-green-700">
                    Total Revenue
                  </span>
                  <span className="font-bold text-green-900">
                    {formatAmount(getAdvancedStats.totalRevenue)}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-xl">
                  <span className="font-medium text-yellow-700">
                    Average Ticket Value
                  </span>
                  <span className="font-bold text-yellow-900">
                    {formatAmount(getAdvancedStats.avgTicketValue)}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6 border border-white/20">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <PieChart className="h-5 w-5 text-blue-600" />
                Package Performance
              </h3>
              <div className="space-y-3">
                {Object.entries(getAdvancedStats.packageStats).map(
                  ([packageName, stats]) => (
                    <div
                      key={packageName}
                      className="flex justify-between items-center p-3 bg-gray-50 rounded-xl"
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {packageName}
                        </p>
                        <p className="text-sm text-gray-600">
                          {stats.count} tickets • {stats.activities} activities
                        </p>
                      </div>
                      <span className="font-bold text-gray-900">
                        {formatAmount(stats.revenue)}
                      </span>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Results Summary */}
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 mb-6 border border-white/20">
            <div className="flex justify-between items-center">
              <p className="text-gray-700">
                Showing{" "}
                <span className="font-bold">{paginatedTickets.length}</span> of{" "}
                <span className="font-bold">
                  {filteredAndSortedTickets.length}
                </span>{" "}
                tickets
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 w-max">Sort by:</span>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </div>
            </div>
          </div>

          {/* Tickets Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
            {paginatedTickets.map((ticket) => (
              <div
                key={ticket._id}
                className="group relative bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden border border-white/20 hover:border-white/40 transform hover:-translate-y-2"
              >
                <div
                  className={`bg-gradient-to-r ${getTicketTypeColor(
                    ticket
                  )} p-6 relative overflow-hidden`}
                >
                  <div className="absolute inset-0 bg-black/10"></div>
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>

                  <div className="relative z-10 flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Star className="h-5 w-5 text-yellow-300 fill-current" />
                        <span className="text-white/90 text-sm font-medium uppercase tracking-wide">
                          {ticket.count_unlimited_ticket > 0
                            ? "Combine Package"
                            : "Coins Package"}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-1">
                        {ticket.ticket_holder_name}
                      </h3>

                      <p className="text-white/80 text-sm">
                        {formatDate(ticket.fun_date)}
                      </p>
                    </div>

                    <div className="text-right">
                      <div className="flex items-center gap-1 justify-end mb-2">
                        {ticket.payment_verified ? (
                          <Check className="h-5 w-5 text-green-300" />
                        ) : (
                          <Clock className="h-5 w-5 text-yellow-300" />
                        )}
                        <span className="text-white/90 text-sm">
                          {ticket.payment_verified ? "Verified" : "Pending"}
                        </span>
                      </div>
                      <p className="text-2xl font-bold text-white">
                        {formatAmount(ticket.total_amount)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="space-y-4">
                    {/* Contact Info */}
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <Phone className="h-4 w-4 text-gray-600" />
                      <span className="font-medium text-gray-900">
                        {ticket.phone_no}
                      </span>
                    </div>

                    {/* Ticket Counts */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center p-3 bg-purple-50 rounded-xl">
                        <p className="text-2xl font-bold text-purple-600">
                          {ticket.count_unlimited_ticket}
                        </p>
                        <p className="text-sm text-purple-700 font-medium">
                          Unlimited
                        </p>
                      </div>
                      <div className="text-center p-3 bg-blue-50 rounded-xl">
                        <p className="text-2xl font-bold text-blue-600">
                          {ticket.count_regular_ticket}
                        </p>
                        <p className="text-sm text-blue-700 font-medium">
                          Regular
                        </p>
                      </div>
                    </div>

                    {/* Package Names */}
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">
                        Packages:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {getPackageNames(ticket.details).map(
                          (packageName, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium"
                            >
                              {packageName}
                            </span>
                          )
                        )}
                      </div>
                    </div>

                    {/* Payment Info */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Cash Amount:</span>
                        <span className="font-medium text-gray-900">
                          {formatAmount(ticket.cash_amount)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Online Amount:</span>
                        <span className="font-medium text-gray-900">
                          {formatAmount(ticket.online_amount)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm border-t pt-2">
                        <span className="text-gray-600">Payment Mode:</span>
                        <span className="font-medium text-gray-900 capitalize">
                          {ticket.payment_mode}
                        </span>
                      </div>
                    </div>

                    {/* Activities */}
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl">
                      <div className="flex items-center gap-2">
                        <Activity className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium text-green-700">
                          Total Activities
                        </span>
                      </div>
                      <span className="text-lg font-bold text-green-600">
                        {getTotalActivities(ticket.details)}
                      </span>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => setSelectedTicket(ticket)}
                        className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
                      >
                        <Eye className="h-4 w-4" />
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all flex items-center gap-2"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </button>

                  {/* Page Numbers */}
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum =
                        Math.max(1, Math.min(totalPages - 4, currentPage - 2)) +
                        i;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-2 rounded-lg transition-all ${
                            currentPage === pageNum
                              ? "bg-blue-500 text-white"
                              : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all flex items-center gap-2"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Ticket Detail Modal */}
          {selectedTicket &&
            createPortal(
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
                <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center rounded-t-3xl">
                    <h2 className="text-2xl font-bold text-gray-900">
                      Ticket Details - #{selectedTicket.short_id}
                    </h2>
                    <button
                      onClick={() => setSelectedTicket(null)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-all"
                    >
                      <X className="h-6 w-6 text-gray-500" />
                    </button>
                  </div>

                  <div className="p-6 space-y-6">
                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                          Basic Information
                        </h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Name:</span>
                            <span className="font-medium ">
                              {selectedTicket.ticket_holder_name}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Ticket ID:</span>
                            <span className="font-medium">
                              #{selectedTicket.short_id}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Phone:</span>
                            <span className="font-medium">
                              {selectedTicket.phone_no}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Date:</span>
                            <span className="font-medium">
                              {formatDate(selectedTicket.fun_date)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              Payment Status:
                            </span>
                            <span
                              className={`font-medium ${
                                selectedTicket.payment_verified
                                  ? "text-green-600"
                                  : "text-orange-600"
                              }`}
                            >
                              {selectedTicket.payment_verified
                                ? "Verified"
                                : "Pending"}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                          Payment Information
                        </h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total Amount:</span>
                            <span className="font-bold text-lg">
                              {formatAmount(selectedTicket.total_amount)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Cash Amount:</span>
                            <span className="font-medium">
                              {formatAmount(selectedTicket.cash_amount)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              Online Amount:
                            </span>
                            <span className="font-medium">
                              {formatAmount(selectedTicket.online_amount)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Payment Mode:</span>
                            <span className="font-medium capitalize">
                              {selectedTicket.payment_mode}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Ticket Counts */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-purple-50 p-4 rounded-xl text-center">
                        <p className="text-3xl font-bold text-purple-600">
                          {selectedTicket.count_unlimited_ticket}
                        </p>
                        <p className="text-purple-700 font-medium">
                          Unlimited Tickets
                        </p>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-xl text-center">
                        <p className="text-3xl font-bold text-blue-600">
                          {selectedTicket.count_regular_ticket}
                        </p>
                        <p className="text-blue-700 font-medium">
                          Regular Tickets
                        </p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-xl text-center">
                        <p className="text-3xl font-bold text-green-600">
                          {selectedTicket.count}
                        </p>
                        <p className="text-green-700 font-medium">
                          Total Count
                        </p>
                      </div>
                    </div>

                    {/* Package Details */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                        Package Details
                      </h3>
                      <div className="grid gap-4">
                        {selectedTicket.details.map((detail, index) => (
                          <div
                            key={index}
                            className="bg-gray-50 p-4 rounded-xl"
                          >
                            <div className="flex justify-between items-start mb-3">
                              <div>
                                <span
                                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                                    detail.packageType === "unlimited"
                                      ? "bg-purple-100 text-purple-700"
                                      : "bg-blue-100 text-blue-700"
                                  }`}
                                >
                                  {detail.packageType === "unlimited"
                                    ? "Unlimited Package"
                                    : "Regular Package"}
                                </span>
                                {detail.serialno && (
                                  <span className="ml-2 text-sm text-gray-600">
                                    Serial: {detail.serialno}
                                  </span>
                                )}
                              </div>
                              {detail.unlimitedPackageData && (
                                <span className="text-xl font-bold text-gray-900">
                                  {formatAmount(
                                    detail.unlimitedPackageData.price
                                  )}
                                </span>
                              )}
                            </div>

                            {detail.unlimitedPackageData && (
                              <div>
                                <h4 className="font-medium text-gray-900 mb-2">
                                  {detail.unlimitedPackageData.name}
                                </h4>
                                <p className="text-sm text-gray-600 mb-3">
                                  {
                                    detail.unlimitedPackageData.activities
                                      .length
                                  }{" "}
                                  activities included
                                </p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                  {detail.unlimitedPackageData.activities.map(
                                    (activity, actIndex) => (
                                      <div
                                        key={actIndex}
                                        className="bg-white p-2 rounded text-center"
                                      >
                                        <p className="text-xs text-gray-600">
                                          Activity {actIndex + 1}
                                        </p>
                                        <p className="font-medium text-sm">
                                          {activity.count === 999
                                            ? "∞"
                                            : activity.count}
                                          x
                                        </p>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>,
              document.body
            )}
        </div>
      </div>
    </div>
  );
};

export default Ticket;
